import {
  ArrowUturnLeftIcon,
  BookmarkIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid'
import cloneDeep from 'clone-deep'
import type { EmailValue } from 'components/EmailTo'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { type BaseFile,itemCountPerPage } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { useSharedLoan } from 'context/SharedLoanContext'
import { usePermissions } from 'hooks/usePermissions'
import { ReminderDetailModal } from 'pages/Reminders/ReminderDetailModal'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  addNewNote,
  deleteNote,
  getAdminEmails,
  getInternalUserEmails,
  getNotes,
  openS3Document,
  updateNote,
  updateNotePinned,
  updateReadStatus,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, ButtonGroup, CopyableText, Input2, Pagination, Tooltip } from 'stories/components'
import {
  confirm,
  convert2EmailMap,
  formatTime,
  renderHeader,
  replyTo,
  stripHtmlTags,
  timeAgo,
  truncateWithEllipsis,
} from 'utils'
import { downloadCSV } from 'utils/copyClipboard'
import { setLoanNumber } from 'utils/setLoanNumber'

import { AddNote } from './AddNote'
import { belongsOnPage } from './belongsOnPage'
import { INote, NotePageType, PINNED_NOTES_CHANGED_EVENT } from './types'

const tabs: Record<string, string> = {
  loan: 'Loan',
  email: 'Email',
}

const csvCell = (value: unknown) => {
  let text = String(value ?? '')
  if (/^[=+\-@]/.test(text)) text = `'${text}`
  return `"${text.replace(/"/g, '""')}"`
}

const notePlainText = (html?: string) => {
  const withBreaks = (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n')
  return stripHtmlTags(withBreaks)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// Long emails, signatures, and pasted tables used to stretch the row past the date column.
const noteBodyClass =
  'max-w-full min-w-0 overflow-x-auto break-words [overflow-wrap:anywhere] [&_img]:h-auto [&_img]:max-w-full [&_pre]:whitespace-pre-wrap'

const loanNoteColumnWidths = (showActions: boolean) =>
  showActions ? ['5%', '16%', '13%', '45%', '13%', '8%'] : ['5%', '18%', '14%', '48%', '15%']

const splitNoteBy = (noteBy: string) => {
  const splits = (noteBy || '').split(' - ')
  return {
    accountType: accountTypes[splits[0]] || splits[0] || '',
    email: splits.slice(1).join(' - '),
  }
}

export default function Notes({
  child = false,
  loanNumber,
  hasAction = true,
  notePageType = NotePageType.GLOBAL,
}: {
  child?: boolean
  loanNumber?: number
  hasAction?: boolean
  notePageType?: NotePageType
}) {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const canAct = hasAction && !isShared
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<INote[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [emailSubjects, setEmailSubjects] = useState<string[]>([])
  const [sharedNotes, setSharedNotes] = useState<INote[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [editingData, setEditingData] = useState<INote | null>(null)
  const [replyData, setReplyData] = useState<INote | null>(null)
  const [filters, setFilters] = useState({
    orderBy: 'createdAt',
    orderDir: '-1',
    type: 'loan',
    pageNum: 0,
    count: itemCountPerPage,
    query: '',
  })
  // null until the email tab picks a thread from the subjects list (newest first).
  const [subject, setSubject] = useState<string | null>(null)
  const [collapseIndex, setCollapseIndex] = useState(-1)
  const [expandAll, setExpandAll] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [isEmail, setIsEmail] = useState(false)
  const [isReminderOpen, setIsReminderOpen] = useState(false)
  const [userEmails, setUserEmails] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const { hasPermission } = usePermissions()
  const canPin = hasPermission('Loan.Servicing.Notes.Pin')
  const canDelete = hasPermission('Loan.Servicing.Notes.Delete')

  const isLoan = useMemo(() => {
    return filters.type === 'loan'
  }, [filters])

  const _loanNumber = loanNumber ? loanNumber : Number(setLoanNumber())

  // Anything emailed to this address is filed back on the loan, so it shows on every notes
  // screen (not just the global Email tab) for anyone who works out of servicing or REO notes.
  // Hidden from a shared loan: the address carries our own loan number, which a peer never sees.
  const loanEmail = useMemo(() => (isShared ? '' : replyTo('loan', _loanNumber)), [isShared, _loanNumber])

  useEffect(() => {
    if (isShared) return
    Promise.all([getAdminEmails(), getInternalUserEmails()]).then(([adminEmails, internalEmails]) =>
      setUserEmails(Array.from(new Set([...adminEmails, ...internalEmails]))),
    )
  }, [isShared])

  // Debounce the search box into filters.query so each keystroke does not hit the API.
  useEffect(() => {
    if (isShared) return
    const timeOutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.query === searchQuery) return prev
        return { ...prev, query: searchQuery, pageNum: 0 }
      })
    }, 700)
    return () => clearTimeout(timeOutId)
  }, [searchQuery, isShared])

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      const notes = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []
      const temp = notes.filter((item: any) => belongsOnPage(item, notePageType))
      setSharedNotes(temp)
      setLoading(false)
      return
    }
    setLoanNumber()
    refetch()
  }, [filters, subject, notePageType, isShared, shared?.pageKey, shared?.pageData])

  // Shared loans arrive as a full payload; page that list in the browser.
  useEffect(() => {
    if (!isShared) return
    const query = (filters.query || searchQuery || '').toLowerCase()
    let list = sharedNotes.filter((item: any) => (item.type || 'loan') === filters.type)
    if (filters.type === 'email') {
      const subjects = [...new Set(list.map((item) => item.subject ?? ''))]
      setEmailSubjects(subjects)
      const activeSubject =
        subject !== null && subjects.includes(subject) ? subject : subjects[0] ?? ''
      if (activeSubject !== subject) setSubject(activeSubject)
      list = list.filter((item) => (item.subject ?? '') === activeSubject)
    }
    if (query) {
      list = list.filter(
        (item) =>
          notePlainText(item.content).toLowerCase().includes(query) ||
          stripHtmlTags(item.subject ?? '')
            .toLowerCase()
            .includes(query) ||
          item.noteBy.toLowerCase().includes(query),
      )
    }
    const dir = parseInt(filters.orderDir) === 1 ? 1 : -1
    const key = filters.orderBy || 'createdAt'
    list = [...list].sort((a: any, b: any) => {
      const av = key === 'createdAt' ? new Date(a[key]).getTime() : String(a[key] ?? '').toLowerCase()
      const bv = key === 'createdAt' ? new Date(b[key]).getTime() : String(b[key] ?? '').toLowerCase()
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
    setTotalCount(list.length)
    const start = filters.pageNum * filters.count
    setData(list.slice(start, start + filters.count))
  }, [isShared, sharedNotes, filters, subject, searchQuery])

  // Keep the pin icons in sync when a note is unpinned from the banner at the top of the loan.
  useEffect(() => {
    if (isShared) return
    const onPinChanged = (event: Event) => {
      const detail = (event as CustomEvent)?.detail
      if (!detail || detail.source === 'notes') return
      setData((prev) => prev.map((item) => (item.id === detail.id ? { ...item, isPinned: !!detail.isPinned } : item)))
    }
    window.addEventListener(PINNED_NOTES_CHANGED_EVENT, onPinChanged)
    return () => window.removeEventListener(PINNED_NOTES_CHANGED_EVENT, onPinChanged)
  }, [isShared])

  const buildListFilters = (newFilters: Record<string, any> | null = null) => {
    const next = { ...(newFilters || filters) }
    const params: Record<string, any> = {
      orderBy: next.orderBy,
      orderDir: next.orderDir,
      type: next.type,
      pageNum: next.pageNum,
      count: next.count,
      query: next.query || '',
      notePageType,
    }
    // Omit subject until a thread is chosen so the first email response can return subjects.
    if (next.type === 'email' && subject !== null) params.subject = subject
    return params
  }

  const refetch = (newFilters: Record<string, any> | null = null) => {
    if (isShared) return
    const requestFilters = newFilters || filters
    setLoading(true)
    getNotes(_loanNumber, buildListFilters(requestFilters))
      .then((res) => {
        const list: INote[] = Array.isArray(res) ? res : res?.data || []
        const total = Array.isArray(res) ? list.length : Number(res?.total) || 0
        if (!Array.isArray(res) && Array.isArray(res?.subjects)) {
          const subjects = res.subjects.map((s: string | null) => s ?? '')
          setEmailSubjects(subjects)
          if (requestFilters.type === 'email' && (subject === null || !subjects.includes(subject))) {
            setData([])
            setTotalCount(0)
            setSubject(subjects[0] ?? '')
            return
          }
        }
        if (list.length === 0 && total > 0 && requestFilters.pageNum > 0) {
          setFilters((prev) => ({ ...prev, pageNum: prev.pageNum - 1 }))
          return
        }
        setData(list)
        setTotalCount(total)
      })
      .finally(() => setLoading(false))
  }

  const onAdd = () => {
    if (!canAct) return
    setEditingData(null)
    setReplyData(null)
    setIsEmail(!isLoan)
    setIsOpen(true)
    setSelectedIndex(-1)
  }

  const onCloseModal = (isNeedRefetch: boolean) => {
    setIsOpen(false)
    setEditingData(null)
    if (isNeedRefetch) refetch()
  }

  const onEdit = (id: number) => {
    if (!canAct) return
    const editData = data.find((item) => item.id === id)
    setEditingData(editData || null)
    setReplyData(null)
    setIsEmail(false)
    setIsOpen(true)
    setSelectedIndex(id)
  }

  const onSubmit = async (
    subject: string,
    content: string,
    files: BaseFile[],
    notifiers: EmailValue,
    isEmail: boolean,
    replyData: Record<string, any>,
    notePageType: NotePageType = NotePageType.GLOBAL,
    links: any[] = [],
    mentions: string[] = [],
    mentionGroups: string[] = [],
  ) => {
    if (!canAct) return
    if (selectedIndex == -1) {
      await addNewNote(
        _loanNumber,
        subject,
        content,
        files,
        notifiers,
        isEmail,
        replyData,
        notePageType,
        links,
        mentions,
        mentionGroups,
      )
    } else {
      await updateNote(selectedIndex, subject, content, files, notifiers)
    }
    setIsOpen(false)
    setSelectedIndex(-1)
    refetch()
  }

  const onDelete = async (id: number, index: number) => {
    if (!canAct || !canDelete) return
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove this item?
        <br />
        No. {rowNumber(index)}
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await deleteNote(id)
    refetch()
  }

  // Pinned notes show in a banner at the top of every loan screen.
  const onTogglePin = async (id: number, isPinned: boolean) => {
    if (!canAct) return
    setLoading(true)
    try {
      await updateNotePinned(id, isPinned)
      const temp = cloneDeep(data)
      temp.forEach((item) => {
        if (item.id === id) item.isPinned = isPinned
      })
      setData(temp)
      window.dispatchEvent(
        new CustomEvent(PINNED_NOTES_CHANGED_EVENT, { detail: { loanNumber: _loanNumber, source: 'notes' } }),
      )
    } finally {
      setLoading(false)
    }
  }

  const onSort = (key: string, dir: number) => {
    setFilters((prev) => ({ ...prev, orderBy: key, orderDir: `${dir}`, pageNum: 0 }))
  }

  const onPageNavigate = (pageNum: number) => {
    setFilters((prev) => ({ ...prev, pageNum }))
  }

  const onCountPerPageUpdate = (count: number) => {
    setFilters((prev) => ({ ...prev, count, pageNum: 0 }))
  }

  const onTypeChange = (type: string) => {
    setExpandAll(false)
    setCollapseIndex(-1)
    if (type === 'email') setSubject(null)
    setFilters((prev) => ({ ...prev, type, pageNum: 0 }))
  }

  const onSubjectChange = (next: string) => {
    if (next === subject) return
    setExpandAll(false)
    setCollapseIndex(-1)
    setSubject(next)
    setFilters((prev) => ({ ...prev, pageNum: 0 }))
  }

  const rowNumber = (index: number) => filters.pageNum * filters.count + index + 1

  const handleCollapse = async (id: number, isRead: boolean) => {
    if (!isRead) {
      setLoading(true)
      await updateReadStatus(id)
      setLoading(false)
      const temp = cloneDeep(data)
      temp.forEach((item) => {
        if (item.id === id) item.isRead = true
      })
      setData(temp)
    }

    setExpandAll(false)
    if (collapseIndex == id) setCollapseIndex(-1)
    else setCollapseIndex(id)
  }

  const onToggleExpandAll = () => {
    setCollapseIndex(-1)
    setExpandAll(!expandAll)
  }

  const onExportCsv = async () => {
    setLoading(true)
    try {
      let notes: any[] = data
      if (!isShared) {
        const query = { orderBy: filters.orderBy, orderDir: filters.orderDir }
        const [loanNotes, emailNotes] = await Promise.all([
          getNotes(_loanNumber, { ...query, type: 'loan' }),
          getNotes(_loanNumber, { ...query, type: 'email' }),
        ])
        notes = [...(loanNotes || []), ...(emailNotes || [])].filter((item) =>
          belongsOnPage(item, notePageType),
        )
      }
      if (!notes.length) {
        toast('There are no notes to export.', { type: 'info' })
        return
      }

      const dir = parseInt(filters.orderDir) === 1 ? 1 : -1
      const key = filters.orderBy || 'createdAt'
      const sortValue = (item: any) => {
        const value = item[key]
        if (value == null) return ''
        if (key === 'createdAt') return new Date(value).getTime()
        return String(value).toLowerCase()
      }
      notes.sort((a, b) => {
        const av = sortValue(a)
        const bv = sortValue(b)
        if (av < bv) return -1 * dir
        if (av > bv) return 1 * dir
        return 0
      })

      const header = ['No', 'Type', 'Note By', 'Email', 'Subject', 'Content', 'Created Date']
      const rows = notes.map((item, index) => {
        const { accountType, email } = splitNoteBy(item.noteBy)
        return [
          index + 1,
          item.type === 'email' ? 'Email' : 'Loan',
          accountType,
          email,
          notePlainText(item.subject),
          notePlainText(item.content),
          formatTime(item.createdAt),
        ].map(csvCell)
      })
      downloadCSV([header.map(csvCell), ...rows], `Notes-${_loanNumber}.csv`)
    } finally {
      setLoading(false)
    }
  }

  const onReply = (id: number) => {
    const replyData = data.find((item) => item.id === id)
    setEditingData(null)
    setReplyData(replyData || null)
    setIsEmail(true)
    setIsOpen(true)
    setSelectedIndex(-1)
  }

  const sortableHeaders = [
    { title: 'Note by', key: 'noteBy' },
    { title: 'Subject', key: 'subject' },
    { title: 'Content', key: 'content' },
    { title: 'Created Date', key: 'createdAt' },
  ]

  const showSenderEMail = (html: string) => {
    const regex =
      /<p[^>]*(?:id="[^"]*sender_email[^"]*"[^>]*|title="([^"]*)")[^>]*(?:id="[^"]*sender_email[^"]*"[^>]*|title="([^"]*)")[^>]*>([\s\S]*?)<\/p>/g

    let updatedHtmlString = html

    updatedHtmlString = updatedHtmlString.replace(regex, (match, title1, title2) => {
      const titleValue = title1 || title2
      let decodedContent = titleValue

      try {
        decodedContent = decodeURIComponent(escape(atob(titleValue.trim())))
      } catch (e) {}

      return match.replace(/>[\s\S]*?<\/p>/, `>${decodedContent}</p>`)
    })

    return updatedHtmlString
  }

  return (
    <div className={`notes-container ${!child && 'px-2 py-6'}`}>
      {!child && <Overview title="Notes" />}
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white ${!child && 'shadow1 rounded mb-4 p-4'}`}>
          <LayoutLoading show={loading} />
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>Notes</span>
            {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
              <ButtonGroup title={tabs} value={filters.type} onChange={onTypeChange} />
              {!!loanEmail && (
                <span className="flex min-w-0 items-center gap-1.5">
                  <EnvelopeIcon className="w-4 h-4 shrink-0 text-shade-blue" />
                  <span className="text-gray-500">Loan Email:</span>
                  <CopyableText copyText={loanEmail}>
                    <span className="text-shade-blue italic break-all">{loanEmail}</span>
                  </CopyableText>
                </span>
              )}
            </div>
            <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-end sm:gap-4">
              {canAct && hasPermission('Loan.Servicing.Notes.Add') && (
                <Input2
                  type="text"
                  title="Search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(v) => setSearchQuery(v)}
                  className="w-full sm:w-72"
                />
              )}
              <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 pb-1">
                <span className="cursor-pointer text-shade-blue hover:underline" onClick={onExportCsv}>
                  Export CSV
                </span>
                {canAct && hasPermission('Loan.Servicing.Notes.Add') && (
                  <Button link onClick={onAdd}>
                    {isLoan ? (
                      '+ Add Note'
                    ) : (
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="w-4 h-4" />
                        Send Email
                      </div>
                    )}
                  </Button>
                )}
                {canAct && (
                  <span
                    className="cursor-pointer text-shade-blue hover:underline"
                    onClick={() => setIsReminderOpen(true)}
                  >
                    + Add Reminder
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-4 lg:flex-row">
            {filters.type === 'email' && (
              <div className="w-full overflow-auto rounded-md p-2 shadow1 scroll-h lg:min-h-[600px] lg:w-[300px]">
                {emailSubjects.map((v) => (
                  <p
                    key={v || '__primary__'}
                    className={`p-2 text-sm cursor-pointer hover:bg-gray-200 rounded-md mb-2 ${
                      subject === v && 'pl-6 bg-gray-200'
                    }`}
                    onClick={() => onSubjectChange(v)}
                  >
                    {v ? v : 'Primary'}
                  </p>
                ))}
              </div>
            )}
            <div className="parties-container min-w-0 flex-1 shadow-md sm:rounded-lg">
              <table className="w-full table-fixed text-sm text-left text-gray-900 dark:text-gray-400">
                <colgroup>
                  {(isLoan ? loanNoteColumnWidths(canAct) : ['100%']).map((width, index) => (
                    <col key={index} style={{ width }} />
                  ))}
                </colgroup>
                {isLoan && (
                  <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="sticky top-0 z-10 bg-slate-50 px-2 py-3 dark:bg-gray-700">
                        No
                      </th>
                      {sortableHeaders.map(({ title, key }, index) =>
                        renderHeader({
                          title,
                          sortable: true,
                          key,
                          sortOrder: filters.orderBy == key ? parseInt(filters.orderDir) : 0,
                          index,
                          onSort: (key: string, dir: number) => onSort(key, dir),
                          className: 'sticky top-0 z-10 bg-slate-50 dark:bg-gray-700',
                          alignClassName: key === 'content' ? 'justify-between w-full' : '',
                          additionalChild:
                            key === 'content' && data.length > 0 ? (
                              <span
                                className="text-shade-blue cursor-pointer hover:underline text-[11px] normal-case font-medium tracking-normal"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onToggleExpandAll()
                                }}
                              >
                                {expandAll ? 'Collapse All' : 'Expand All'}
                              </span>
                            ) : null,
                        }),
                      )}
                      {canAct && (
                        <th scope="col" className="sticky top-0 z-10 bg-slate-50 px-2 py-3 dark:bg-gray-700">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                )}
                <tbody className="text-[14.5px] text-gray-900">
                  {data.map((item, index) => {
                      let accountType = '',
                        email = ''
                      const isSelected = expandAll || collapseIndex == item.id
                      const [noteDate, noteTime] = formatTime(item.createdAt).split(' ')
                      try {
                        const splits = item.noteBy.split(' - ')
                        accountType = accountTypes[splits[0]] || splits[0]
                        email = splits[1]
                      } catch {}
                      return (
                        <tr key={item.id ?? index} className={`border-b ${index % 2 == 0 ? 'bg-gray-50' : ''}`}>
                          {isLoan && (
                            <>
                              <td className="px-2 py-3 align-top">{rowNumber(index)}</td>
                              <td className="overflow-hidden px-2 py-3 align-top">
                                <div className="min-w-0 break-words [overflow-wrap:anywhere]">
                                  <div className="mb-1 w-fit max-w-full border-b border-gray-300">{accountType}</div>
                                  {email}
                                </div>
                              </td>
                              <td className="overflow-hidden px-2 py-3 align-top">
                                <Tooltip message={item?.subject ? stripHtmlTags(item.subject) : ''}>
                                  <div
                                    className="break-words [overflow-wrap:anywhere]"
                                    dangerouslySetInnerHTML={{
                                      __html: item?.subject
                                        ? truncateWithEllipsis(stripHtmlTags(item.subject), 25)
                                        : '',
                                    }}
                                  />
                                </Tooltip>
                              </td>
                            </>
                          )}
                          <td className={`${isLoan ? 'overflow-hidden px-2' : 'px-6'} min-w-0 py-3 align-top`}>
                            <div
                              className="min-w-0 cursor-pointer"
                              onClick={() => handleCollapse(item.id, item.isRead)}
                            >
                              {!isLoan && (
                                <div className="mb-2 flex min-w-0 flex-wrap items-start justify-between gap-2">
                                  <div className="min-w-0 break-words [overflow-wrap:anywhere]">
                                    <p>
                                      <span className="font-bold">{accountType}</span>
                                      {!item.isRead && (
                                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full ml-2">
                                          Unread
                                        </span>
                                      )}
                                      {!item.readByReceiver && (
                                        <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full ml-2">
                                          Unread by Receiver
                                        </span>
                                      )}
                                      {isSelected && <span className="text-gray-500 text-sm">{` <${item.from}>`}</span>}
                                    </p>
                                    {isSelected && <p className="text-gray-500 text-sm">to {item.to.join(', ')}</p>}
                                  </div>
                                  <div className="flex shrink-0 items-center gap-1">
                                    <span>
                                      {formatTime(item.createdAt)}
                                      {` (${timeAgo(item.createdAt)})`}
                                    </span>
                                    <div className="cursor-pointer" onClick={() => onReply(item.id)}>
                                      <div className="hover-shadow1 p-1 rounded">
                                        <Tooltip message="Reply">
                                          <ArrowUturnLeftIcon className="w-4 h-5" />
                                        </Tooltip>
                                      </div>
                                    </div>
                                    {isSelected && canDelete && (
                                      <span
                                        className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                                        onClick={() => onDelete(item.id, index)}
                                      >
                                        <Tooltip message="Delete">
                                          <TrashIcon className="w-4 h-4"></TrashIcon>
                                        </Tooltip>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              <div
                                className={noteBodyClass}
                                dangerouslySetInnerHTML={{
                                  __html: isSelected
                                    ? showSenderEMail(item.content)
                                    : truncateWithEllipsis(notePlainText(item.content), 200),
                                }}
                              />
                            </div>
                            {!isLoan && isSelected && (
                              <>
                                {item.history && (
                                  <div
                                    className="flex text-gray-900 cursor-pointer mt-2"
                                    onClick={() => setShowMore(!showMore)}
                                  >
                                    <div className="rounded-lg bg-gray-200 hover:bg-gray-300">
                                      <EllipsisHorizontalIcon className="w-8 h-4" />
                                    </div>
                                  </div>
                                )}
                                {showMore && (
                                  <div
                                    className={`${noteBodyClass} cursor-pointer`}
                                    dangerouslySetInnerHTML={{ __html: showSenderEMail(item.history) }}
                                  />
                                )}
                              </>
                            )}
                            {isLoan && item.notifiers && Object.keys(item.notifiers).length > 0 && (
                              <div className="mt-1 break-words text-gray-500 [overflow-wrap:anywhere]">
                                {convert2EmailMap(JSON.stringify(item.notifiers)).text}
                              </div>
                            )}
                            {!!item.files.length && (
                              <div className="mt-2 flex min-w-0 items-start gap-2">
                                <span>Files:</span>
                                <div className="min-w-0">
                                  {item.files.map((file) => (
                                    <p
                                      className="break-all text-[12px] hover:underline text-shade-blue italic font-semibold cursor-pointer"
                                      onClick={() => openS3Document(file.fileKey)}
                                      key={`${file.fileKey}`}
                                    >
                                      - {file.name}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                          {isLoan && (
                            <>
                              <td className="px-2 py-3 align-top leading-tight">
                                <div className="whitespace-nowrap">{noteDate}</div>
                                {noteTime && <div className="whitespace-nowrap text-gray-500">{noteTime}</div>}
                              </td>
                              {canAct && (
                                <td className="px-2 py-3 align-top">
                                  <div className="flex flex-wrap gap-1">
                                    {canDelete && (
                                      <span
                                        className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                                        onClick={() => onDelete(item.id, index)}
                                      >
                                        <TrashIcon className="w-4 h-4"></TrashIcon>
                                      </span>
                                    )}
                                    <span
                                      className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                                      onClick={() => onEdit(item.id)}
                                    >
                                      <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                                    </span>
                                    {canPin && (
                                      <span
                                        className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                                        onClick={() => onTogglePin(item.id, !item.isPinned)}
                                      >
                                        <Tooltip message={item.isPinned ? 'Unpin from top' : 'Pin to top'}>
                                          {item.isPinned ? (
                                            <BookmarkSolidIcon className="w-4 h-4" />
                                          ) : (
                                            <BookmarkIcon className="w-4 h-4" />
                                          )}
                                        </Tooltip>
                                      </span>
                                    )}
                                  </div>
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      )
                    })}
                </tbody>
              </table>
              {totalCount > 0 && (
                <Pagination
                  totalCount={totalCount}
                  itemCountPerPage={filters.count}
                  pageNum={filters.pageNum}
                  onNavigate={onPageNavigate}
                  onCountPerPageUpdate={onCountPerPageUpdate}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <AddNote
          onUpdate={onSubmit}
          onClose={onCloseModal}
          item={editingData}
          index={selectedIndex}
          isEmail={isEmail}
          replyData={replyData}
          loanNumber={loanNumber}
          notePageType={notePageType}
        />
      )}
      {isReminderOpen && (
        <ReminderDetailModal
          reminderId={-1}
          userEmails={userEmails}
          onModalClose={() => setIsReminderOpen(false)}
          onModalOk={() => setIsReminderOpen(false)}
          loanNumber={_loanNumber}
        />
      )}
    </div>
  )
}
